import { create } from "zustand"

import { tokens } from "@/lib/tokens.generated"

/*
 * The ground under the console (Master Design System §09 · §12). Right of the
 * rail the map IS the page: a constructed still on an index, the live Stadia
 * basemap once a page hands it a mappable point. The ground is ONE persistent
 * element — it lives in the persistent layout, and pages claim it through
 * `useGround` — so navigating index → show is not a page swap: the still
 * dissolves into the territory that was there all along.
 *
 * This store is the one place its state lives: what the current page asked
 * for, and how the sheet sits on it (over the ground, docked at the desk edge
 * so the territory can be worked, or docked with directions open).
 *
 * The console shell is the floor: while one is mounted the still is under
 * every page, so a page only speaks up when it has a point to resolve onto.
 * Pages outside the shell (the public form, auth) have no ground at all.
 */

export interface GroundPoint {
  lng: number
  lat: number
}

/** Where a drive starts — seeded from the job's region, never asked for. */
export interface GroundOffice {
  state: string
  label: string
  lng: number
  lat: number
}

/** What a subject page hands the ground: the job's point and what the band says about it. */
export interface GroundSubject {
  point: GroundPoint
  label: string
  address: string | null
  office: GroundOffice | null
  /** Every office, so the drive's "from" can be changed for the visit. */
  offices: GroundOffice[]
}

export type GroundSpec =
  | { kind: "still" }
  | ({ kind: "resolve" } & GroundSubject)

export const STILL: GroundSpec = { kind: "still" }

/** How the sheet sits on a subject page. Anything but `sheet` is "docked". */
export type GroundMode = "sheet" | "expanded" | "directions"

/** The clear band above the sheet on a subject page, where the ground shows the job. */
export const BAND = parseInt(tokens.spacing["ground-band"], 10)
/** The crumb strip that stays visible when the sheet is docked — the handle that brings it back. */
export const HANDLE = parseInt(tokens.spacing["ground-handle"], 10)
/** The sheet's margin at lg (`lg:my-3`). */
const SHEET_MARGIN = 12

interface GroundState {
  spec: GroundSpec | null
  /** The page that currently holds the ground; a release from anyone else is ignored. */
  owner: symbol | null
  /** How many console shells are mounted — one, or two for the instant a visit swaps pages. */
  shells: number
  mode: GroundMode
  /** How far the sheet travels when docked: the window less the band, the handle and its margin. */
  dockOffset: number
  /** Where the drive starts when the reader has changed it — for this visit only, never stored. */
  origin: GroundOffice | null
  claim: (owner: symbol, spec: GroundSpec) => void
  release: (owner: symbol) => void
  enterShell: () => void
  leaveShell: () => void
  setMode: (mode: GroundMode) => void
  setOrigin: (origin: GroundOffice | null) => void
}

const same = (a: GroundSpec | null, b: GroundSpec | null) =>
  JSON.stringify(a) === JSON.stringify(b)

export const useGroundStore = create<GroundState>((set, get) => ({
  spec: null,
  owner: null,
  shells: 0,
  mode: "sheet",
  dockOffset: 0,
  origin: null,

  claim(owner, spec) {
    const current = get()
    if (current.owner === owner && same(current.spec, spec)) return
    set({ owner, spec, mode: "sheet", origin: null })
  },

  release(owner) {
    if (get().owner !== owner) return
    set({ owner: null, spec: null, mode: "sheet", origin: null })
  },

  enterShell() {
    set({ shells: get().shells + 1 })
  },

  leaveShell() {
    set({ shells: Math.max(get().shells - 1, 0) })
  },

  setOrigin(origin) {
    set({ origin })
  },

  setMode(mode) {
    const docked = mode !== "sheet"
    set({
      mode,
      dockOffset: docked
        ? Math.max(window.innerHeight - BAND - HANDLE - SHEET_MARGIN, 0)
        : get().dockOffset,
    })
  },
}))

/** What the ground shows: the page's claim, else the still under any console shell, else nothing. */
export const groundOf = (state: Pick<GroundState, "spec" | "shells">) =>
  state.spec ?? (state.shells > 0 ? STILL : null)

export const useGroundSpec = () => useGroundStore(groundOf)
